import React, { useEffect, useState } from "react";
import axios from "axios";
import { Table, Spinner, Button, Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import { MapContainer, TileLayer, Marker, Popup, Polyline } from "react-leaflet";
import L from "leaflet";
import { useRef } from "react";
import AdminPageShell, { AdminSurface } from "../../../components/Admin/AdminPageShell";
import { getApiUrl } from "../../../utils/site";

const DeliveryRoutePage = () => {
  const [deliveries, setDeliveries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDelivery, setSelectedDelivery] = useState(null);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [mapModal, setMapModal] = useState(false);
  const [mapDelivery, setMapDelivery] = useState(null);
  const [updatingId, setUpdatingId] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState({});
  const [proofFiles, setProofFiles] = useState({});
  const fileInputs = useRef({});

  useEffect(() => {
    fetchDeliveries();
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => setCurrentLocation([pos.coords.latitude, pos.coords.longitude]),
        () => setCurrentLocation(null)
      );
    }
  }, []);

  const fetchDeliveries = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(getApiUrl("/api/delivery/deliveries"), {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDeliveries(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      setDeliveries([]);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = (orderId, value) => {
    setSelectedStatus((prev) => ({ ...prev, [orderId]: value }));
  };

  const handleFileChange = (orderId, file) => {
    setProofFiles((prev) => ({ ...prev, [orderId]: file }));
  };

  const handleUpdateStatus = async (order) => {
    setUpdatingId(order.id);
    try {
      const status = selectedStatus[order.id];
      if (status === "delivered" && !proofFiles[order.id]) {
        alert("Please upload a proof image for delivery.");
        setUpdatingId(null);
        return;
      }
      if (status === "delivered" && proofFiles[order.id]) {
        const formData = new FormData();
        formData.append("proof", proofFiles[order.id]);
        const token = localStorage.getItem("token");
        await axios.post(getApiUrl(`/api/orders/${order.id}/proof`), formData, {
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
        });
      }
      const token = localStorage.getItem("token");
      await axios.put(
        getApiUrl(`/api/orders/${order.id}/delivery-status`),
        { delivery_status: status },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await fetchDeliveries();
      alert("Order updated.");
    } catch (error) {
      alert("Failed to update order.");
    } finally {
      setUpdatingId(null);
    }
  };

  const getLatLng = (delivery) => {
    const lat = Number(delivery.delivery_map_url?.split("q=")[1]?.split(",")[0]);
    const lng = Number(delivery.delivery_map_url?.split(",")[1]);
    if (isNaN(lat) || isNaN(lng)) return null;
    return [lat, lng];
  };

  const currentDeliveries = deliveries.filter((d) => ["pending", "shipped"].includes(d.delivery_status));
  const previousDeliveries = deliveries.filter((d) => ["delivered", "cancelled"].includes(d.delivery_status));
  const route =
    selectedDelivery && currentLocation && getLatLng(selectedDelivery)
      ? [currentLocation, getLatLng(selectedDelivery)]
      : [];

  return (
    <AdminPageShell
      eyebrow="Field operations"
      title="Delivery Route"
      description="Update delivery status, upload proof, and review maps for current and previous fulfilment tasks."
    >
      {loading ? (
        <div className="roha-admin-empty">
          <Spinner color="info" /> Loading deliveries...
        </div>
      ) : (
        <>
          <AdminSurface title="Current deliveries" subtitle={`${currentDeliveries.length} active route tasks`}>
            <div className="roha-admin-table-wrap">
              <Table responsive className="roha-admin-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Customer</th>
                    <th>Phone</th>
                    <th>Address</th>
                    <th>Map</th>
                    <th>Products</th>
                    <th>Total</th>
                    <th>Status</th>
                    <th>Delivery</th>
                    <th>Update</th>
                  </tr>
                </thead>
                <tbody>
                  {currentDeliveries.length ? (
                    currentDeliveries.map((delivery, idx) => (
                      <tr key={delivery.id}>
                        <td data-label="#">{idx + 1}</td>
                        <td data-label="Customer">{delivery.customer_name}</td>
                        <td data-label="Phone">{delivery.customer_phone || "-"}</td>
                        <td data-label="Address">{delivery.delivery_address_line1}<br />{delivery.delivery_city}, {delivery.delivery_state}</td>
                        <td data-label="Map">
                          {delivery.delivery_map_url && getLatLng(delivery) ? (
                            <Button color="primary" size="sm" onClick={() => { setMapDelivery(delivery); setMapModal(true); setSelectedDelivery(delivery); }}>
                              View map
                            </Button>
                          ) : "-"}
                        </td>
                        <td data-label="Products">
                          <ul className="mb-0 ps-3 roha-admin-table-list">
                            {(delivery.items || []).map((item, i) => (
                              <li key={i}>{item.product_name} x {item.quantity}</li>
                            ))}
                          </ul>
                        </td>
                        <td data-label="Total">ETB {delivery.total}</td>
                        <td data-label="Status"><span className="roha-admin-pill">{delivery.status}</span></td>
                        <td data-label="Delivery"><span className="roha-admin-pill">{delivery.delivery_status}</span></td>
                        <td data-label="Update">
                          <select
                            value={selectedStatus[delivery.id] || delivery.delivery_status}
                            onChange={(e) => handleStatusChange(delivery.id, e.target.value)}
                            disabled={updatingId === delivery.id}
                            className="form-select form-select-sm mb-2"
                          >
                            <option value="pending">Pending</option>
                            <option value="shipped">Shipped</option>
                            <option value="delivered">Delivered</option>
                            <option value="cancelled">Cancelled</option>
                          </select>
                          {selectedStatus[delivery.id] === "delivered" ? (
                            <input
                              type="file"
                              accept="image/*"
                              className="form-control form-control-sm mb-2"
                              onChange={(e) => handleFileChange(delivery.id, e.target.files[0])}
                              ref={(el) => (fileInputs.current[delivery.id] = el)}
                            />
                          ) : null}
                          <Button color="success" size="sm" disabled={updatingId === delivery.id} onClick={() => handleUpdateStatus(delivery)}>
                            {updatingId === delivery.id ? "Updating..." : "Update"}
                          </Button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="10" className="roha-admin-empty">No current deliveries assigned.</td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          </AdminSurface>

          <AdminSurface title="Previous deliveries" subtitle={`${previousDeliveries.length} completed or cancelled records`}>
            <div className="roha-admin-table-wrap">
              <Table responsive className="roha-admin-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Customer</th>
                    <th>Phone</th>
                    <th>Address</th>
                    <th>Map</th>
                    <th>Products</th>
                    <th>Total</th>
                    <th>Status</th>
                    <th>Delivery</th>
                  </tr>
                </thead>
                <tbody>
                  {previousDeliveries.length ? (
                    previousDeliveries.map((delivery, idx) => (
                      <tr key={delivery.id}>
                        <td data-label="#">{idx + 1}</td>
                        <td data-label="Customer">{delivery.customer_name}</td>
                        <td data-label="Phone">{delivery.customer_phone || "-"}</td>
                        <td data-label="Address">{delivery.delivery_address_line1}<br />{delivery.delivery_city}, {delivery.delivery_state}</td>
                        <td data-label="Map">
                          {delivery.delivery_map_url && getLatLng(delivery) ? (
                            <Button color="primary" size="sm" onClick={() => { setMapDelivery(delivery); setMapModal(true); }}>
                              View map
                            </Button>
                          ) : "-"}
                        </td>
                        <td data-label="Products">
                          <ul className="mb-0 ps-3 roha-admin-table-list">
                            {(delivery.items || []).map((item, i) => (
                              <li key={i}>{item.product_name} x {item.quantity}</li>
                            ))}
                          </ul>
                        </td>
                        <td data-label="Total">ETB {delivery.total}</td>
                        <td data-label="Status"><span className="roha-admin-pill">{delivery.status}</span></td>
                        <td data-label="Delivery"><span className="roha-admin-pill">{delivery.delivery_status}</span></td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="9" className="roha-admin-empty">No previous deliveries.</td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          </AdminSurface>

          <AdminSurface title="Route map" subtitle="Live map based on current location and selected delivery">
            <div style={{ height: 400, width: "100%" }}>
              <MapContainer center={currentLocation || [9.03, 38.74]} zoom={13} style={{ height: "100%", width: "100%" }}>
                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                {currentLocation ? (
                  <Marker
                    position={currentLocation}
                    icon={L.icon({ iconUrl: "https://cdn-icons-png.flaticon.com/512/684/684908.png", iconSize: [32, 32] })}
                  >
                    <Popup>Your location</Popup>
                  </Marker>
                ) : null}
                {selectedDelivery && getLatLng(selectedDelivery) ? (
                  <Marker
                    position={getLatLng(selectedDelivery)}
                    icon={L.icon({ iconUrl: "https://cdn-icons-png.flaticon.com/512/854/854878.png", iconSize: [32, 32] })}
                  >
                    <Popup>{selectedDelivery.customer_name}</Popup>
                  </Marker>
                ) : null}
                {route.length === 2 ? <Polyline positions={route} color="blue" /> : null}
              </MapContainer>
            </div>
          </AdminSurface>
        </>
      )}

      <Modal isOpen={mapModal} toggle={() => setMapModal(false)} size="lg">
        <ModalHeader toggle={() => setMapModal(false)}>Delivery location map</ModalHeader>
        <ModalBody>
          {mapDelivery && getLatLng(mapDelivery) ? (
            <div style={{ height: 350, width: "100%" }}>
              <MapContainer center={getLatLng(mapDelivery)} zoom={15} style={{ height: "100%", width: "100%" }}>
                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                <Marker
                  position={getLatLng(mapDelivery)}
                  icon={L.icon({ iconUrl: "https://cdn-icons-png.flaticon.com/512/854/854878.png", iconSize: [32, 32] })}
                >
                  <Popup>Delivery location</Popup>
                </Marker>
              </MapContainer>
            </div>
          ) : (
            <div>No map data available.</div>
          )}
        </ModalBody>
        <ModalFooter>
          {mapDelivery?.delivery_map_url ? (
            <a href={mapDelivery.delivery_map_url} target="_blank" rel="noopener noreferrer" className="btn btn-primary">
              Get directions
            </a>
          ) : null}
          <Button color="secondary" onClick={() => setMapModal(false)}>Close</Button>
        </ModalFooter>
      </Modal>
    </AdminPageShell>
  );
};

export default DeliveryRoutePage;
